import { ChangeDetectionStrategy, Component, DestroyRef, LOCALE_ID, OnInit, inject, signal, computed } from "@angular/core";
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  FormGroupDirective,
  NgForm,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from "@angular/forms";
import { ErrorStateMatcher } from "@angular/material/core";
import { MatSnackBar } from "@angular/material/snack-bar";
import { debounceTime, distinctUntilChanged } from "rxjs/operators";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { getFormattedDate } from "../../../utils";

// Services
import { StoreUserService } from '@app/core/state/store-user.service';
import { StoreAuthService } from '@app/core/auth/store-auth.service';
import {
  DatabaseCacheService,
  Section,
} from "../../../data/cache/database-cache.service";
import {
  RecordTypes,
  FileTypes,
  getRecordTypeDisplay,
  resolveRecordTypeLocale,
  RecordTypeLocale,
} from "../../../types";

import dayjs from "dayjs";
import { from } from "rxjs";
import { ListUsersService } from "../../../data/user/list-users.service";

import { GetRecordsWithFilesSizeService } from "../../../data/record/get-records-with-files-size.service";
import { CreateRecordsDownloadRequestService } from "../../../data/record/create-records-download-request.service";
import { Router } from "@angular/router";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { MatDatepickerModule, MatDatepickerIntl } from "@angular/material/datepicker";
import { MatNativeDateModule, MAT_DATE_LOCALE, NativeDateAdapter, DateAdapter, MAT_DATE_FORMATS } from "@angular/material/core";
import { SHARED_IMPORTS } from "src/app/shared/standalone-imports";
import { CREATE_RECORDS_DOWNLOAD_REQUEST_I18N } from "./create-records-download-request.i18n";
import { JapaneseDatepickerIntl } from "@app/i18n";

// カスタムDateAdapter - 日付セルから「日」を除去
class LocalizedDateAdapter extends NativeDateAdapter {
  // カレンダーの日付セルに表示する文字列（1〜31）
  override getDateNames(): string[] {
    return Array.from({ length: 31 }, (_, i) => String(i + 1));
  }

  override format(date: Date, displayFormat: object): string {
    const locale = (this.locale || 'ja-JP').toLowerCase();
    const isEnglish = locale.startsWith('en');
    const formatStr = displayFormat as unknown as string;
    if (formatStr === 'input') {
      // 入力フィールド用のフォーマット
      const year = date.getFullYear();
      const month = this._to2digit(date.getMonth() + 1);
      const day = this._to2digit(date.getDate());
      return `${year}/${month}/${day}`;
    } else if (formatStr === 'monthYearLabel') {
      // カレンダーヘッダー用
      const year = date.getFullYear();
      const month = date.getMonth() + 1;
      if (isEnglish) {
        return new Intl.DateTimeFormat('en-US', { month: 'short', year: 'numeric' }).format(date);
      }
      return `${year}年${month}月`;
    } else if (formatStr === 'monthYearA11yLabel') {
      const year = date.getFullYear();
      const month = date.getMonth() + 1;
      if (isEnglish) {
        return new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric' }).format(date);
      }
      return `${year}年${month}月`;
    } else if (formatStr === 'dateA11yLabel') {
      const year = date.getFullYear();
      const month = date.getMonth() + 1;
      const day = date.getDate();
      if (isEnglish) {
        return new Intl.DateTimeFormat('en-US', { year: 'numeric', month: 'long', day: 'numeric' }).format(date);
      }
      return `${year}年${month}月${day}日`;
    }
    // デフォルト（日付セル）- 数字のみ
    return String(date.getDate());
  }

  private _to2digit(n: number): string {
    return n < 10 ? '0' + n : String(n);
  }
}

// カスタム日付フォーマット
const JAPANESE_DATE_FORMATS = {
  parse: {
    dateInput: 'input',
  },
  display: {
    dateInput: 'input',
    monthYearLabel: 'monthYearLabel',
    dateA11yLabel: 'dateA11yLabel',
    monthYearA11yLabel: 'monthYearA11yLabel',
  },
};

@Component({
  selector: "app-create-records-download-request",
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: "./create-records-download-request.component.html",
  styleUrls: ["./create-records-download-request.component.scss"],
  imports: [...SHARED_IMPORTS, MatProgressSpinnerModule, MatDatepickerModule, MatNativeDateModule],
  providers: [
    {
      provide: MAT_DATE_LOCALE,
      useFactory: (localeId: string) =>
        (localeId || '').toLowerCase().startsWith('en') ? 'en-US' : 'ja-JP',
      deps: [LOCALE_ID],
    },
    { provide: MatDatepickerIntl, useClass: JapaneseDatepickerIntl },
    { provide: DateAdapter, useClass: LocalizedDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: JAPANESE_DATE_FORMATS },
  ],
})
export class CreateRecordsDownloadRequest implements OnInit {
  private readonly destroyRef = inject(DestroyRef);

  // i18n定数（型安全・IDE補完対応）
  protected readonly i18n = CREATE_RECORDS_DOWNLOAD_REQUEST_I18N;

  currentStepIndex = 0; // 現在のステップインデックス

  // Promise
  promise = Promise.resolve();

  // section info
  selectedSection: Section;

  lastNameENFormControl = new FormControl("", [
    Validators.required,
    Validators.pattern("[a-zA-Z0-9 ]*"),
  ]);

  firstNameENFormControl = new FormControl("", [
    Validators.required,
    Validators.pattern("[a-zA-Z0-9 ]*"),
  ]);

  allOrSelectedUsers = "all";
  users = [];

  // ユーザー選択UI用
  userSearchControl = new FormControl("");
  userSearchQuery = signal("");
  isUserListExpanded = signal(false);
  isLoadingUsers = signal(true);

  // フィルタリングされたユーザーリスト
  filteredUsers = computed(() => {
    const query = this.userSearchQuery().toLowerCase().trim();
    if (!query) {
      return this.users;
    }
    return this.users.filter(user =>
      user.name.toLowerCase().includes(query) ||
      user.userId.toLowerCase().includes(query)
    );
  });

  // サイズ見積もり
  sizeEstimation = signal<any>(null);
  isEstimating = signal(false);
  estimationElapsedSeconds = signal(0);
  isSending = signal(false);
  private estimationTimerInterval: ReturnType<typeof setInterval> | null = null;

  createConditionFormGroup: FormGroup;
  sexOptions = [
    { display: this.i18n.sex.male, value: "male" },
    { display: this.i18n.sex.female, value: "female" },
    { display: this.i18n.sex.other, value: "other" },
  ];
  // recordTypes = [ 'RSST', 'GOST', 'VS', 'SSTP', 'TOMASS', 'MWST', 'FOOD_TEST', 'MEAL_MONITORING', 'VITAL' ];
  recordTypeLocale: RecordTypeLocale = resolveRecordTypeLocale();
  recordTypeDispValues = Array.from(
    new Set(
      RecordTypes.map((record) =>
        getRecordTypeDisplay(record.code, this.recordTypeLocale),
      ),
    ),
  );

  selectedOption: string;
  matcher = new MyErrorStateMatcher();
  codeMode = true;
  // postParam = [];
  // postParamData;

  accountStatus = [
    { value: "OPEN", viewValue: this.i18n.status.active },
    { value: "CLOSE", viewValue: this.i18n.status.inactive },
  ];
  selectedStatus = "";

  accountGroup = [
    { value: "STAFF", viewValue: this.i18n.accountGroup.staff },
    { value: "USER", viewValue: this.i18n.accountGroup.user },
  ];
  selectedGroup = "";

  accountManageRole = false;
  userManageRole = false;
  measureRole = false;
  referRole = false;

  userId: string;
  sectionId: string;

  fileTypes = FileTypes;

  constructor(
    public storeUser: StoreUserService,
    public storeAuth: StoreAuthService,
    public snackBar: MatSnackBar,
    private common: DatabaseCacheService,
    private fb: FormBuilder,
    private listUsersService: ListUsersService,
    private getSizeService: GetRecordsWithFilesSizeService,
    private createDlRequestService: CreateRecordsDownloadRequestService,
    private router: Router,
  ) {}

  ngOnInit(): void {
    const defaultFromDate = this.getDefaultFromDate();
    this.createConditionFormGroup = new FormGroup({
      fromDate: new FormControl<Date | null>(defaultFromDate),
      toDate: new FormControl<Date | null>(null),

      allOrSelectedUsers: new FormControl("all"),
      selectedUsersList: new FormControl(""),

      allOrSelectedRecordTypes: new FormControl("all"),
    });

    FileTypes.forEach((dataType) =>
      this.createConditionFormGroup.addControl(
        dataType.value,
        new FormControl(false),
      ),
    ); // 各dataTypeに対してコントロールを追加
    this.recordTypeDispValues.forEach((recordType) =>
      this.createConditionFormGroup.addControl(
        recordType,
        new FormControl(false),
      ),
    ); // 各recordTypeに対してコントロールを追加

    // this.createConditionFormGroup.get('sex').valueChanges.pipe(
    //   distinctUntilChanged()).subscribe(selectedOption => {
    //     this.createConditionFormGroup.get('sex').setValue(selectedOption);
    //   });

    // section
    this.selectedSection = this.common.getSelectedSection();
    this.common.parametersUpdated.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((noData) => {
      // Sectionに関わるデータの読み込み
      this.selectedSection = this.common.getSelectedSection();
    });

    this.listUsers();

    // ユーザー検索のdebounce
    this.userSearchControl.valueChanges.pipe(
      debounceTime(200),
      distinctUntilChanged(),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(value => {
      this.userSearchQuery.set(value || "");
    });
  }

  private getDefaultFromDate(): Date {
    const fromDate = new Date();
    fromDate.setDate(fromDate.getDate() - 30);
    return fromDate;
  }

  get types() {
    return this.createConditionFormGroup.get("types") as FormArray;
  }

  get fromDateFormControl() {
    return this.createConditionFormGroup.get("fromDate");
  }

  get toDateFormControl() {
    return this.createConditionFormGroup.get("toDate");
  }

  get sexFormControl() {
    return this.createConditionFormGroup.get("sex");
  }

  get selectedUserIds() {
    return this.createConditionFormGroup.get("selectedUsersList");
  }

  get selectedFileTypes() {
    let selectedFileTypes = [];

    FileTypes.forEach((dataType) => {
      if (this.createConditionFormGroup.get(dataType.value).value) {

        selectedFileTypes.push(dataType.value);
      }
    });

    return selectedFileTypes;
  }

  get selectedRecordTypes() {
    let selectedRecordTypes = [];

    this.recordTypeDispValues.forEach((recordType) => {
      if (this.createConditionFormGroup.get(recordType).value) {

        const filteredTypes = RecordTypes.filter(
          (r) =>
            getRecordTypeDisplay(r.code, this.recordTypeLocale) === recordType,
        ).map((r) => r.code);

        selectedRecordTypes.push(...filteredTypes);
      }
    });

    return selectedRecordTypes;
  }

  listUsers() {
    this.users = [];
    this.isLoadingUsers.set(true);

    //this.listUsersService.listUsers().subscribe(
    this.listUsersService
      .listUserWithUserData(
        this.common.getSelectedSection().sectionId,
        true,
        false,
        false,
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.users = [];
          let hasAnonymous = false;

          const users = res as any;

          for (let i = 0; i < users.length; i++) {
            const user = users[i];

            var date = dayjs(user.contractInfo.contractStartAt);
            var dateStr = date.isValid() ? date.format("YYYY/MM/DD") : "ERROR";

            let userId = user.user.userId;

            if (userId === "ANONYMOUS") {
              hasAnonymous = true;

              this.users.unshift({
                userId: "--",
                name:
                  user.user.code ??
                  user.user.lastName + " " + user.user.firstName,
                createdDate: dateStr,
                status: "AVAILABLE",
                anonymous: true,
                contributors: null,
                links: null,
              });
            } else {
              this.users.push({
                userId: userId,
                name:
                  user.user.code ??
                  user.user.lastName + " " + user.user.firstName,
                createdDate: dateStr,
                status: user.contractInfo.contractStatus.toUpperCase(),
                anonymous: false,
                contributors: user.contributors,
                links: user.linkDocuments,
              });
            }
          }

          if (!hasAnonymous) {
            this.users.unshift({
              userId: "--",
              name: this.i18n.userName.anonymous,
              createdDate: "--",
              status: "NOT AVAILABLE",
              anonymous: true,
              contributors: null,
              links: null,
            });
          }
          this.isLoadingUsers.set(false);
        },
        error: () => {
          this.isLoadingUsers.set(false);
        },
      });
  }

  update() {
    this.createDownloadRequest();
  }

  submit() {
  }

  getSizeEstimation() {
    let postParamsData = this.getPostParams();

    // Start loading state and timer
    this.isEstimating.set(true);
    this.estimationElapsedSeconds.set(0);
    this.startEstimationTimer();

    try {
      this.getSizeService
        .getRecordsWithFilesSize(postParamsData)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (res: any) => {
            let size = res.totalSize;
            let unit = "B";
            size /= 1024;
            unit = "KB";
            if (size > 1024) {
              size /= 1024;
              unit = "MB";
            }
            if (size > 1024) {
              size /= 1024;
              unit = "GB";
            }
            this.sizeEstimation.set({
              ...res,
              totalSizeFormatted: size.toFixed(2) + " " + unit
            });
            this.stopEstimationTimer();
            this.isEstimating.set(false);
          },
          error: () => {
            this.stopEstimationTimer();
            this.isEstimating.set(false);
          }
        });
    } catch {
      this.stopEstimationTimer();
      this.isEstimating.set(false);
    }
  }

  private startEstimationTimer() {
    this.stopEstimationTimer();
    this.estimationTimerInterval = setInterval(() => {
      this.estimationElapsedSeconds.update(v => v + 1);
    }, 1000);
  }

  private stopEstimationTimer() {
    if (this.estimationTimerInterval) {
      clearInterval(this.estimationTimerInterval);
      this.estimationTimerInterval = null;
    }
  }

  createDownloadRequest() {

    if (this.sizeEstimation() == null) {
      return;
    }

    let postParamsData = this.getPostParams();

    this.resetConditions();

    this.isSending.set(true);
    this.createDlRequestService
      .createRecordsDownloadRequest(postParamsData)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.isSending.set(false);
          let snack = this.snackBar.open(
            this.i18n.message.downloadRequestCreated,
            this.i18n.button.close,
            { duration: 30000 },
          );
          snack.afterDismissed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            this.router.navigate([
              "/section-admin/",
              this.selectedSection.sectionId,
              "records-download-requests",
            ]);
          });
        },
        error: () => {
          this.isSending.set(false);
        },
      });
  }

  resetConditions() {
    this.onAllUserSelected();
    this.onAllRecordTypesSelected();

    const defaultFromDate = this.getDefaultFromDate();
    this.createConditionFormGroup.get("fromDate").setValue(defaultFromDate);
    this.createConditionFormGroup.get("toDate").setValue(null);

    FileTypes.forEach((dataTypes) =>
      this.createConditionFormGroup.get(dataTypes.value).setValue(false),
    );

    this.currentStepIndex = 0;
    this.conditionChanged();
  }

  // Custom stepper navigation methods
  goToStep(index: number) {
    if (index <= this.currentStepIndex) {
      this.currentStepIndex = index;
    }
  }

  nextStep() {
    if (this.currentStepIndex === 0) {
      // Validate form before proceeding
      if (this.createConditionFormGroup.valid) {
        this.currentStepIndex = 1;
        if (this.sizeEstimation() === null) {
          this.getSizeEstimation();
        }
      }
    } else if (this.currentStepIndex === 1) {
      if (this.sizeEstimation() !== null) {
        this.currentStepIndex = 2;
      }
    }
  }

  prevStep() {
    if (this.currentStepIndex > 0) {
      this.currentStepIndex--;
    }
  }

  onRecordTypeChange(type: string, event: Event) {
    let allOrSelected = "all";
    this.recordTypeDispValues.forEach((recordType) => {
      if (this.createConditionFormGroup.get(recordType).value) {
        allOrSelected = "selected";
      }
    });
    this.createConditionFormGroup
      .get("allOrSelectedRecordTypes")
      .setValue(allOrSelected);

    this.conditionChanged();
  }

  onAllRecordTypesSelected() {
    this.recordTypeDispValues.forEach((recordType) =>
      this.createConditionFormGroup.get(recordType).setValue(false),
    );

    this.conditionChanged();
  }

  onUserChanged(event: Event) {
    let selectedUserIds = this.selectedUserIds.value || [];

    if (selectedUserIds.length == 0) {
      this.createConditionFormGroup.get("allOrSelectedUsers").setValue("all");
    } else {
      this.createConditionFormGroup
        .get("allOrSelectedUsers")
        .setValue("selected");
    }

    this.conditionChanged();
  }

  onAllUserSelected() {
    this.createConditionFormGroup.get("selectedUsersList").setValue([]);
    this.isUserListExpanded.set(false);
    this.conditionChanged();
  }

  toggleUserListExpanded() {
    this.isUserListExpanded.update(v => !v);
  }

  selectAllUsers() {
    const allUserIds = this.filteredUsers().map(user => user.userId);
    this.createConditionFormGroup.get("selectedUsersList").setValue(allUserIds);
    this.createConditionFormGroup.get("allOrSelectedUsers").setValue("selected");
    this.conditionChanged();
  }

  clearAllUsers() {
    this.createConditionFormGroup.get("selectedUsersList").setValue([]);
    this.createConditionFormGroup.get("allOrSelectedUsers").setValue("all");
    this.conditionChanged();
  }

  isUserSelected(userId: string): boolean {
    const selectedList = this.selectedUserIds.value || [];
    return selectedList.includes(userId);
  }

  toggleUserSelection(userId: string) {
    const currentSelection = this.selectedUserIds.value || [];
    const index = currentSelection.indexOf(userId);

    if (index > -1) {
      currentSelection.splice(index, 1);
    } else {
      currentSelection.push(userId);
    }

    this.createConditionFormGroup.get("selectedUsersList").setValue([...currentSelection]);

    if (currentSelection.length === 0) {
      this.createConditionFormGroup.get("allOrSelectedUsers").setValue("all");
    } else {
      this.createConditionFormGroup.get("allOrSelectedUsers").setValue("selected");
    }

    this.conditionChanged();
  }

  conditionChanged() {
    this.sizeEstimation.set(null);
  }

  getPostParams() {
    let fromDate = null;
    const fromDateValue = this.fromDateFormControl.value;
    if (fromDateValue instanceof Date) {
      fromDate = fromDateValue.toISOString();
    }

    let toDate = null;
    const toDateValue = this.toDateFormControl.value;
    if (toDateValue instanceof Date) {
      toDate = toDateValue.toISOString();
    }

    var postParamData = {
      fromDate: fromDate,
      toDate: toDate,
    } as any;

    if (
      this.createConditionFormGroup.get("allOrSelectedUsers").value ==
      "selected"
    ) {
      let selectedUserIds = this.selectedUserIds.value;
      postParamData.userIds = selectedUserIds;
    }

    let selectedFileTypes = this.selectedFileTypes;
    postParamData.fileTypes = selectedFileTypes;
    postParamData.fileTypes.push("DATA"); //ユーザーが特に選択しなくても CSVファイルはダウンロードに常に含まれるようにする

    // let selectedRecordTypes = this.selectedRecordTypes;
    if (
      this.createConditionFormGroup.get("allOrSelectedRecordTypes").value ==
      "selected"
    ) {
      let selectedRecordTypes = this.selectedRecordTypes;
      postParamData.recordTypes = selectedRecordTypes;
    }

    const urlTree = this.router.createUrlTree([
      `/section-admin/${this.common.getSelectedSection().sectionId}/records-download-requests`,
    ]);
    let relativeUrl = this.router.serializeUrl(urlTree);
    const currentUrl = window.location.href;
    const baseUrl = currentUrl.split("#")[0]; // `#`より前の部分を取得
    postParamData.downloadRequestsUrl = `${baseUrl}#${relativeUrl}`;

    return postParamData;
  }
}

/** Error when invalid control is dirty, touched, or submitted. */
export class MyErrorStateMatcher implements ErrorStateMatcher {
  isErrorState(
    control: FormControl | null,
    form: FormGroupDirective | NgForm | null,
  ): boolean {
    const isSubmitted = form && form.submitted;
    return !!(
      control &&
      control.invalid &&
      (control.dirty || control.touched || isSubmitted)
    );
  }
}
